import { supabase } from "../integrations/supabase/client";

export interface TrendingBook {
  id: number;
  title: string;
  authors?: { id: number; name: string }[];
  categories?: { id: number; name: string }[];
  cover_image?: string;
  cover_bg_color?: string;
  reading_time?: number;
  rating?: number;
  view_count?: number;
  read_count?: number;
  audio_play_count?: number;
  summary_contents?: SummaryContentData[];
  trending_score: number;
  trending_reasons: string[];
  recent_activity: {
    views_24h: number;
    reads_24h: number;
    audio_plays_24h: number;
    saves_24h: number;
  };
}

interface SummaryContentData {
  id: number;
  audio_url?: string;
}

interface TrendingBooksResponse {
  trending_books: TrendingBook[];
  metadata: {
    totalBooksAnalyzed: number;
    period: string;
    generatedAt: string;
  };
}

interface TrendingBooksParams {
  limit?: number;
  period?: "24h" | "7d" | "30d" | "90d";
  category?: number;
}

/**
 * Fetch trending books from the edge function
 * @param params - Query parameters for trending books
 * @returns Promise with trending books data
 */
export const fetchTrendingBooks = async (
  params: TrendingBooksParams = {}
): Promise<TrendingBooksResponse> => {
  try {
    const { limit = 20, period = "24h", category } = params;
    
    // Build query parameters
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      period,
    });
    
    if (category) {
      queryParams.append("category", category.toString());
    }

    const { data, error } = await supabase.functions.invoke(
      `trending-books?${queryParams.toString()}`
    );

    if (error) {
      console.error("Error fetching trending books:", error);
      throw new Error(`Failed to fetch trending books: ${error.message}`);
    }

    return data as TrendingBooksResponse;
  } catch (error) {
    console.error("Error in fetchTrendingBooks:", error);
    throw error;
  }
};

/**
 * Get trending books for a specific category
 * @param categoryId - Category ID to filter by
 * @param limit - Number of books to return
 * @returns Promise with trending books for the category
 */
export const fetchTrendingBooksByCategory = async (
  categoryId: number,
  limit: number = 15
): Promise<TrendingBooksResponse> => {
  return fetchTrendingBooks({
    limit,
    period: "24h",
    category: categoryId,
  });
};

/**
 * Get top trending books (default: 10 books)
 * @param limit - Number of books to return
 * @returns Promise with top trending books
 */
export const fetchTopTrendingBooks = async (
  limit: number = 10
): Promise<TrendingBooksResponse> => {
  return fetchTrendingBooks({
    limit,
    period: "24h",
  });
};

/**
 * Get trending books for different time periods
 * @param period - Time period for trending analysis
 * @param limit - Number of books to return
 * @returns Promise with trending books for the period
 */
export const fetchTrendingBooksByPeriod = async (
  period: "24h" | "7d" | "30d" | "90d",
  limit: number = 20
): Promise<TrendingBooksResponse> => {
  return fetchTrendingBooks({
    limit,
    period,
  });
}; 